import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

/**
 * Pike's studio: while garage mode is up the car is shown in a small dark studio drawn INSTEAD of the city
 * (render/sceneOverride.ts, as Sid's back room): a seamless dark floor running up into a cyclorama, a soft
 * overhead light box, a warm key from above the front, a cool rim from behind that picks out the body lines,
 * a faint warm glow on the floor under the car and a soft contact shadow. The neon underglow pops in the dark.
 *
 * The lights mirror the city's set exactly — two directional lights (the key casts the shadow), one
 * hemisphere, one point light — and the fog is the same kind (exp²), so the car's materials keep the same
 * shader programs: opening the bay compiles nothing new for the car. Nothing exists outside garage mode;
 * everything is built on open and disposed on close. The car's own view (and the wheel batches) are lent
 * to this scene by the vehicle view (vehicles/vehicleView.ts `vehicleSolo`).
 */
export interface Studio {
  readonly scene: THREE.Scene;
  /** Stand the studio round the car (its ground point and heading). */
  place(x: number, z: number, heading: number): void;
  /** 0 = full show lighting, 1 = dark (the neon shot). Eased per frame. */
  setDark(k: number): void;
  update(dt: number): void;
  dispose(): void;
}

const R = 15;
const BG = 0x0a0908;
const BOX = new THREE.Color(0xfff1dc);

/** A radial alpha gradient (white), for the floor glow and the contact shadow. */
function radial(n: number, power: number): THREE.DataTexture {
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const d = Math.min(1, Math.hypot((x + 0.5) / n - 0.5, (y + 0.5) / n - 0.5) * 2);
      const i = (y * n + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(Math.pow(1 - d, power) * 255);
    }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
}

export function buildStudio(renderer: THREE.WebGLRenderer | null, shadows: boolean, carLength: number, carWidth: number): Studio {
  const scene = new THREE.Scene();
  scene.name = 'pike-studio';
  scene.background = new THREE.Color(BG);
  scene.fog = new THREE.FogExp2(BG, 0.018);
  const disposables: { dispose(): void }[] = [];
  const keep = <T extends { dispose(): void }>(d: T): T => {
    disposables.push(d);
    return d;
  };
  const rig = new THREE.Group();
  scene.add(rig);

  // ---- the cyclorama: a floor that curves up into the wall, darker toward the top (vertex colours)
  const prof: THREE.Vector2[] = [];
  const cove = 3.2;
  prof.push(new THREE.Vector2(0, 0));
  for (let i = 0; i <= 8; i++) {
    const a = (i / 8) * (Math.PI / 2);
    prof.push(new THREE.Vector2(R - cove + Math.sin(a) * cove, cove - Math.cos(a) * cove));
  }
  prof.push(new THREE.Vector2(R, 14));
  const cycGeo = keep(new THREE.LatheGeometry(prof, 48));
  const col = new Float32Array(cycGeo.getAttribute('position').count * 3);
  const pos = cycGeo.getAttribute('position');
  const low = new THREE.Color(0x24201b);
  const high = new THREE.Color(0x060505);
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const r = Math.hypot(pos.getX(i), pos.getZ(i));
    c.copy(low).lerp(high, Math.min(1, Math.max(0, (r - 4) / (R - 4)) * 0.55 + Math.min(1, pos.getY(i) / 8) * 0.45));
    col.set([c.r, c.g, c.b], i * 3);
  }
  cycGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const cycMat = keep(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.42, metalness: 0, side: THREE.DoubleSide, envMapIntensity: 0.35 }));
  const cyc = new THREE.Mesh(cycGeo, cycMat);
  cyc.receiveShadow = true;
  scene.add(cyc);

  // ---- an overhead light box (seen in the high shots) and a thin light line round the cove
  const boxMat = keep(new THREE.MeshBasicMaterial({ color: 0xfff1dc, fog: false, side: THREE.DoubleSide }));
  const boxGeo = keep(new THREE.PlaneGeometry(carLength * 1.3, carWidth * 2.2).rotateX(Math.PI / 2));
  const box = new THREE.Mesh(boxGeo, boxMat);
  box.position.y = 7.5;
  rig.add(box);

  // ---- under the car: a warm glow on the floor, a soft contact shadow
  const glowTex = keep(radial(64, 1.8));
  const glowMat = keep(new THREE.MeshBasicMaterial({ map: glowTex, color: 0xffcf96, transparent: true, opacity: 0.32, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
  const glowGeo = keep(new THREE.PlaneGeometry(carWidth * 5, carLength * 2.6).rotateX(-Math.PI / 2));
  const glow = new THREE.Mesh(glowGeo, glowMat);
  glow.position.y = 0.006;
  glow.renderOrder = 1;
  rig.add(glow);
  const shadowTex = keep(radial(64, 1.2));
  const shadowMat = keep(new THREE.MeshBasicMaterial({ map: shadowTex, color: 0x000000, transparent: true, opacity: 0.75, depthWrite: false, fog: false }));
  const shadowGeo = keep(new THREE.PlaneGeometry(carWidth * 1.5, carLength * 1.25).rotateX(-Math.PI / 2));
  const contact = new THREE.Mesh(shadowGeo, shadowMat);
  contact.position.y = 0.01;
  contact.renderOrder = 2;
  rig.add(contact);

  // ---- the lights (the city's set: 2 directional, 1 hemisphere, 1 point — see above)
  const key = new THREE.DirectionalLight(0xfff0dc, 1.8);
  key.position.set(1.5, 9, 5);
  key.target.position.set(0, 0.5, 0);
  key.castShadow = shadows;
  if (shadows) {
    key.shadow.mapSize.set(1024, 1024);
    const sc = key.shadow.camera;
    sc.left = -4;
    sc.right = 4;
    sc.top = 4;
    sc.bottom = -4;
    sc.near = 1;
    sc.far = 20;
    key.shadow.bias = -0.0008;
    key.shadow.normalBias = 0.03;
    key.shadow.radius = 4;
  }
  const rim = new THREE.DirectionalLight(0xb4c8ff, 2.2);
  rim.position.set(-4, 3.5, -7);
  rim.target.position.set(0, 0.6, 0);
  const hemi = new THREE.HemisphereLight(0x5a5650, 0x120f0c, 0.55);
  const fill = new THREE.PointLight(0xffc89a, 1.4, 10, 1.6);
  fill.position.set(0, 3, carLength * 0.8);
  rig.add(key, key.target, rim, rim.target, hemi, fill);

  // Something for the paint to reflect: soft light panels (RoomEnvironment), built once per visit.
  if (renderer) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const env = new RoomEnvironment();
    const rt = pmrem.fromScene(env, 0.04);
    env.dispose();
    pmrem.dispose();
    scene.environment = rt.texture;
    scene.environmentIntensity = 0.55;
    keep(rt);
  }

  let dark = 0;
  let want = 0;
  const apply = (): void => {
    const lit = 1 - dark;
    key.intensity = 0.25 + 0.95 * lit;
    rim.intensity = 0.4 + 1.0 * lit;
    hemi.intensity = 0.12 + 0.3 * lit;
    fill.intensity = 0.2 + 1.2 * lit;
    glowMat.opacity = 0.04 + 0.14 * lit;
    boxMat.color.copy(BOX).multiplyScalar(0.25 + 0.75 * lit);
    scene.environmentIntensity = 0.06 + 0.16 * lit;
  };
  apply();

  return {
    scene,
    place(x, z, heading) {
      rig.position.set(x, 0, z);
      rig.rotation.y = heading;
      cyc.position.set(x, 0, z);
      rig.updateMatrixWorld(true);
    },
    setDark(k) {
      want = Math.max(0, Math.min(1, k));
    },
    update(dt) {
      if (dark === want) return;
      const step = dt * 2.5;
      dark = Math.abs(want - dark) <= step ? want : dark + Math.sign(want - dark) * step;
      apply();
    },
    dispose() {
      scene.environment = null;
      for (const d of disposables) d.dispose();
      key.shadow.map?.dispose();
      scene.clear();
    },
  };
}
